import type { CampaignWindow, FlashSaleRotation } from '../../utils/campaign/flashSaleRotation';

export type CampaignPromoBackground = 'black-gold' | 'purple';

export type CampaignPromoTextPart = {
  text: string;
  bold?: boolean;
};

/** Topbar flag copy. Omit on a campaign that should not take over the flag. */
export type CampaignPromo = {
  textParts: CampaignPromoTextPart[];
  background: CampaignPromoBackground;
};

/** Copy for a campaign's flash-sale block. */
export type CampaignFlashSaleCopy = {
  /**
   * Joined with a space into the single promo line under the product rail — SSD-6744 removed the
   * hero banner these two were written for. That strip does not wrap, so the joined sentence has
   * to stay under roughly 45 characters or it overflows on a 360px screen.
   */
  bannerTitle: string;
  bannerSubtitle: string;
  /** The page-top announcement bar, above the flash-sale block. */
  textBannerCopy: string;
};

/** One window of a daily rotation. The url is the parent campaign's, so it is not repeated here. */
export type CampaignFlashSaleWindow = CampaignWindow & {
  /** The exact WordPress term name: `GET /products/collection` matches on the name, not the slug. */
  collectionName: string;
  collectionSlug: string;
};

/** Rotating flash-sale block. Omit on a campaign that does not run one. */
export type CampaignFlashSale = {
  copy: CampaignFlashSaleCopy;
  /** Daily windows in order. Gaps are allowed; the block simply goes dark between them. */
  rotations: CampaignFlashSaleWindow[];
  /** Also swap the PDP "Same Category" module for this rotation while it is live. */
  showOnPdp?: boolean;
};

/** A flash-sale window flattened for a surface — its campaign's url and copy stamped on. */
export type CampaignFlashSaleRotation = FlashSaleRotation & {
  collectionSlug: string;
  copy: CampaignFlashSaleCopy;
};

export type Campaign = CampaignWindow & {
  /** Human label so the config reads like the campaign calendar. Not rendered. */
  name: string;
  /** Where the flag sends the shopper. */
  campaignUrl: string;
  promo?: CampaignPromo;
  flashSale?: CampaignFlashSale;
};

/**
 * The one campaign calendar. Both the topbar flag (`HOLIDAY_BANNER_ROTATIONS`) and the homepage
 * flash-sale rail (`FLASH_SALE_ROTATIONS`) are derived from this list, so a campaign is scheduled
 * once and every surface follows the same window.
 *
 * All times are SGT (`Asia/Singapore`, fixed UTC+8, no DST), format 'YYYY-MM-DD HH:mm:ss'.
 *
 * **Order is significant.** Windows may overlap and the first match wins, so a campaign that
 * should take priority is listed above the one it overlaps.
 *
 * Past campaigns are deleted once they end rather than kept for history — git has that, and a
 * stale window can only add noise. The topbar falls back to DEFAULT_HOLIDAY_BANNER when the list
 * matches nothing, and the flash-sale rail simply goes dark.
 *
 * Keep the promo half in lockstep with astro-blog `src/data/promo.ts`, which carries its own
 * copy of the same schedule for the blog topbar.
 */
export const CAMPAIGNS: Campaign[] = [
  {
    name: 'The Seoul Festival',
    startDate: '2026-09-17 15:00:00',
    endDate: '2026-09-24 15:00:00',
    campaignUrl: '/campaign/the-seoul-festival/',
    flashSale: {
      copy: {
        bannerTitle: 'New deals every 24h,',
        bannerSubtitle: 'Grab your favorites fast.',
        textBannerCopy: 'Up to 50% off across the selection',
      },
      // Seven collections, one live per day from 3PM SGT to 3PM SGT. Merch swaps the sale prices in
      // WordPress on the same boundary, so a handover needs no deploy.
      rotations: [
        {
          startDate: '2026-09-17 15:00:00',
          endDate: '2026-09-18 15:00:00',
          collectionName: 'Seoul Festival Flash Sale 1',
          collectionSlug: 'seoul-festival-flash-sale-1',
        },
        {
          startDate: '2026-09-18 15:00:00',
          endDate: '2026-09-19 15:00:00',
          collectionName: 'Seoul Festival Flash Sale 2',
          collectionSlug: 'seoul-festival-flash-sale-2',
        },
        {
          startDate: '2026-09-19 15:00:00',
          endDate: '2026-09-20 15:00:00',
          collectionName: 'Seoul Festival Flash Sale 3',
          collectionSlug: 'seoul-festival-flash-sale-3',
        },
        {
          startDate: '2026-09-20 15:00:00',
          endDate: '2026-09-21 15:00:00',
          collectionName: 'Seoul Festival Flash Sale 4',
          collectionSlug: 'seoul-festival-flash-sale-4',
        },
        {
          startDate: '2026-09-21 15:00:00',
          endDate: '2026-09-22 15:00:00',
          collectionName: 'Seoul Festival Flash Sale 5',
          collectionSlug: 'seoul-festival-flash-sale-5',
        },
        {
          startDate: '2026-09-22 15:00:00',
          endDate: '2026-09-23 15:00:00',
          collectionName: 'Seoul Festival Flash Sale 6',
          collectionSlug: 'seoul-festival-flash-sale-6',
        },
        {
          startDate: '2026-09-23 15:00:00',
          endDate: '2026-09-24 15:00:00',
          collectionName: 'Seoul Festival Flash Sale 7',
          collectionSlug: 'seoul-festival-flash-sale-7',
        },
      ],
    },
  },
  {
    name: 'The Seoul Mega Festival',
    startDate: '2026-09-24 15:00:00',
    endDate: '2026-10-01 15:00:00',
    campaignUrl: '/campaign/the-seoul-mega-festival/',
    // No promo: SSD-6746 keeps the top flag on BAU free shipping through week 2.
    flashSale: {
      // SSD-6004: week 2 only — week 1 never ran on the PDP.
      showOnPdp: true,
      // The brief's "New deals every 24 hours!" overflows the one-line strip on a 360px screen, so
      // this keeps week 1's shortened wording.
      copy: {
        bannerTitle: 'New deals every 24h,',
        bannerSubtitle: 'Grab your favorites fast.',
        textBannerCopy: 'Up to 70% off across the selection',
      },
      // Week 2 has its own collection family, one word apart from week 1's: "Seoul Mega Festival".
      rotations: [
        {
          startDate: '2026-09-24 15:00:00',
          endDate: '2026-09-25 15:00:00',
          collectionName: 'Seoul Mega Festival Flash Sale 1',
          collectionSlug: 'seoul-mega-festival-flash-sale-1',
        },
        {
          startDate: '2026-09-25 15:00:00',
          endDate: '2026-09-26 15:00:00',
          collectionName: 'Seoul Mega Festival Flash Sale 2',
          collectionSlug: 'seoul-mega-festival-flash-sale-2',
        },
        {
          startDate: '2026-09-26 15:00:00',
          endDate: '2026-09-27 15:00:00',
          collectionName: 'Seoul Mega Festival Flash Sale 3',
          collectionSlug: 'seoul-mega-festival-flash-sale-3',
        },
        {
          startDate: '2026-09-27 15:00:00',
          endDate: '2026-09-28 15:00:00',
          collectionName: 'Seoul Mega Festival Flash Sale 4',
          collectionSlug: 'seoul-mega-festival-flash-sale-4',
        },
        {
          startDate: '2026-09-28 15:00:00',
          endDate: '2026-09-29 15:00:00',
          collectionName: 'Seoul Mega Festival Flash Sale 5',
          collectionSlug: 'seoul-mega-festival-flash-sale-5',
        },
        {
          startDate: '2026-09-29 15:00:00',
          endDate: '2026-09-30 15:00:00',
          collectionName: 'Seoul Mega Festival Flash Sale 6',
          collectionSlug: 'seoul-mega-festival-flash-sale-6',
        },
        {
          startDate: '2026-09-30 15:00:00',
          endDate: '2026-10-01 15:00:00',
          collectionName: 'Seoul Mega Festival Flash Sale 7',
          collectionSlug: 'seoul-mega-festival-flash-sale-7',
        },
      ],
    },
  },
];

export const FLASH_SALE_TIMEZONE = 'Asia/Singapore';

type CampaignWithFlashSale = Campaign & { flashSale: NonNullable<Campaign['flashSale']> };

const hasFlashSale = (campaign: Campaign): campaign is CampaignWithFlashSale => Boolean(campaign.flashSale);

const toFlashSaleRotations = (campaigns: CampaignWithFlashSale[]): CampaignFlashSaleRotation[] =>
  campaigns.flatMap((campaign) =>
    campaign.flashSale.rotations.map((rotation) => ({
      ...rotation,
      campaignUrl: campaign.campaignUrl,
      copy: campaign.flashSale.copy,
    })),
  );

/**
 * Homepage flash-sale schedule (SSD-6696), derived from the one campaign calendar in `campaigns.ts`.
 *
 * Every campaign running a daily rotation contributes its windows in registry order, each stamped
 * with the parent campaign's url and copy. Carrying the copy on the window — rather than exporting
 * it separately — is what lets a surface resolve the collection and the wording it ships with in the
 * same lookup, so the two cannot drift once a second campaign runs a rotation of its own.
 *
 * Schedule a flash sale in `CAMPAIGNS`, not here: that is what keeps it on the same window as the
 * topbar flag.
 */
export const FLASH_SALE_ROTATIONS: CampaignFlashSaleRotation[] = toFlashSaleRotations(CAMPAIGNS.filter(hasFlashSale));

/**
 * The PDP's flash-sale schedule (SSD-6004): the rotations of campaigns that opt in with
 * `showOnPdp`. While one is live the PDP shows it in place of "Same Category", and falls back to
 * "Same Category" between and after them.
 */
export const PDP_FLASH_SALE_ROTATIONS: CampaignFlashSaleRotation[] = toFlashSaleRotations(
  CAMPAIGNS.filter(hasFlashSale).filter((campaign) => campaign.flashSale.showOnPdp),
);
